import { useEffect, useRef, useState } from 'react';

import { DashboardAnomalies } from '@/features/dashboard/components/dashboard/dashboard-anomalies';
import { DashboardProjectBar } from '@/features/dashboard/components/dashboard/dashboard-project-bar';
import { DashboardProjectBattery } from '@/features/dashboard/components/dashboard/dashboard-project-battery';
import { DashboardProjectKpis } from '@/features/dashboard/components/dashboard/dashboard-project-kpis';
import { DashboardProjectSelector } from '@/features/dashboard/components/dashboard/dashboard-project-selector';
import { NoAnomaliesProjectCard } from '@/features/dashboard/components/dashboard/no-anomalies-project-card';
import { NoKpisProjectCard } from '@/features/dashboard/components/dashboard/no-kpis-project-card';
import { ProjectSlideCardEmpty } from '@/features/dashboard/components/dashboard/project-slide-card-empty';
import { useKpiAnomalies } from '@/features/insights/hooks/use-kpi-anomalies';
import { useKpisWithExistingEntries } from '@/features/projects/project-details/hooks/use-kpis-with-existing-entries';
import { Card, CardContent, CardHeader } from '@/shared/components/ui/card';
import { Skeleton } from '@/shared/components/ui/skeleton';
import {
  ProjectKpiWithMetrics,
  ProjectWithContractors,
} from '@/shared/types/api';

export type BaseProjectSlideCardProps = {
  project: ProjectWithContractors;
  currentCardIndex: number;
  onCardIndexChange: (index: number) => void;
  isPaused: boolean;
  onTogglePause: () => void;
  kpi?: ProjectKpiWithMetrics;
};

export const DashboardProjectSlotCard = ({
  project,
  slotIndex,
  currentCardIndex,
  onCardIndexChange,
  isActive,
  isPaused,
  onTogglePause,
}: BaseProjectSlideCardProps & {
  slotIndex: number;
  isActive: boolean;
}) => {
  const [kpiIndex, setKpiIndex] = useState(0);
  const prevCardIndexRef = useRef(currentCardIndex);

  const projectType = project.type;
  const { data: kpis = [], isPending: isPendingKpis } =
    useKpisWithExistingEntries({
      projectId: project.id,
      projectType,
    });

  useEffect(() => {
    setKpiIndex(0);
    prevCardIndexRef.current = currentCardIndex;
  }, [project.id]);

  useEffect(() => {
    if (prevCardIndexRef.current === 3 && currentCardIndex === 0) {
      setKpiIndex((prev) => prev + 1);
    }
    prevCardIndexRef.current = currentCardIndex;
  }, [currentCardIndex]);

  const selectedKpiIndex = kpis.length > 0 ? kpiIndex % kpis.length : 0;
  const selectedKpi = kpis[selectedKpiIndex];
  const firstKpi = kpis[0];
  const firstKpiId = firstKpi?.id;

  const { data: anomalies = [] } = useKpiAnomalies({
    kpiId: firstKpiId ?? 0,
    projectId: project.id,
    monthsBack: 12,
    periodType: 'monthly',
  });

  const anomalyAnomalies = anomalies.filter((a) => a.is_anomaly);
  const latestAnomaly = anomalyAnomalies[anomalyAnomalies.length - 1];
  const otherAnomalies = anomalyAnomalies.slice(-4, -1).reverse();

  const baseProps: BaseProjectSlideCardProps = {
    project,
    currentCardIndex,
    onCardIndexChange,
    isPaused,
    onTogglePause,
  };

  const currentCard = (() => {
    switch (currentCardIndex) {
      case 0:
        return <DashboardProjectBar {...baseProps} kpi={selectedKpi} />;
      case 1:
        return <DashboardProjectBattery {...baseProps} kpi={selectedKpi} />;
      case 2:
        return <DashboardProjectKpis {...baseProps} kpis={kpis.slice(0, 3)} />;
      case 3:
        return latestAnomaly ? (
          <DashboardAnomalies
            {...baseProps}
            latestAnomaly={latestAnomaly}
            otherAnomalies={otherAnomalies}
          />
        ) : (
          <NoAnomaliesProjectCard {...baseProps} />
        );
      default:
        return null;
    }
  })();

  return (
    <DashboardProjectSelector
      project={project}
      slotIndex={slotIndex}
      isActive={isActive}
    >
      {isPendingKpis ? (
        <Card variant="informationAlt" className="size-full">
          <CardHeader className="flex flex-row items-start justify-between">
            <div className="flex flex-col gap-2">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-28" />
            </div>
            <Skeleton className="h-8 w-24 rounded-full" />
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Skeleton className="h-32 w-full rounded-lg" />
            <div className="flex items-center justify-between">
              <Skeleton className="h-6 w-20" />
              <Skeleton className="h-10 w-32" />
            </div>
          </CardContent>
        </Card>
      ) : selectedKpi ? (
        currentCard
      ) : kpis.length === 0 ? (
        <NoKpisProjectCard project={project} />
      ) : (
        <ProjectSlideCardEmpty />
      )}
    </DashboardProjectSelector>
  );
};
